import { invokeFunction } from '../api'
import { toApiRequestError } from '../errors'

export type Capability =
  | 'run_assessment'
  | 'run_survey'
  | 'capture_evidence'
  | 'assign_tasks'
  | 'create_findings'
  | 'view_all_actions'
  | 'manage_training'
  | 'review_findings'
  | 'release_reports'

export interface CapabilityGrant {
  id: string
  facility_id: string
  user_id: string
  capability: Capability
  granted_by: string
  granted_at: string
}

export const ROLE_DEFAULT_CAPABILITIES: Record<string, Capability[]> = {
  user: [],
  field_surveyor: [
    'run_survey',
    'capture_evidence',
    'create_findings',
  ],
  editor: [
    'run_assessment',
    'run_survey',
    'capture_evidence',
    'create_findings',
    'view_all_actions',
  ],
  admin: [
    'run_assessment',
    'run_survey',
    'capture_evidence',
    'assign_tasks',
    'create_findings',
    'view_all_actions',
    'manage_training',
    'review_findings',
    'release_reports',
  ],
  superadmin: [
    'run_assessment',
    'run_survey',
    'capture_evidence',
    'assign_tasks',
    'create_findings',
    'view_all_actions',
    'manage_training',
    'review_findings',
    'release_reports',
  ],
}

export const ALL_CAPABILITIES: Capability[] = [
  'run_assessment',
  'run_survey',
  'capture_evidence',
  'assign_tasks',
  'create_findings',
  'view_all_actions',
  'manage_training',
  'review_findings',
  'release_reports',
]

export const CAPABILITY_LABELS: Record<Capability, string> = {
  run_assessment: 'Run Daily Assessment',
  run_survey: 'Conduct Guided Survey',
  capture_evidence: 'Capture Evidence',
  assign_tasks: 'Assign Tasks',
  create_findings: 'Create Findings',
  view_all_actions: 'View All Actions',
  manage_training: 'Manage Training',
  review_findings: 'Review Findings',
  release_reports: 'Release Reports',
}

export async function listCapabilities(facilityId?: string, userId?: string): Promise<CapabilityGrant[]> {
  const params = new URLSearchParams()
  if (facilityId) params.set('facility_id', facilityId)
  if (userId) params.set('user_id', userId)
  const query = params.toString()

  const { data, error } = await invokeFunction<{ capabilities: CapabilityGrant[] }>('admin-users', {
    method: 'GET',
    path: `/capabilities${query ? `?${query}` : ''}`,
  })

  if (error) throw toApiRequestError(error, 'Failed to list capabilities')
  return Array.isArray(data?.capabilities) ? data.capabilities : []
}

export async function grantCapability(facilityId: string, userId: string, capability: Capability): Promise<CapabilityGrant> {
  const { data, error } = await invokeFunction<CapabilityGrant>('admin-users', {
    method: 'POST',
    path: '/capabilities',
    body: { facility_id: facilityId, user_id: userId, capability },
  })

  if (error) throw toApiRequestError(error, 'Failed to grant capability')
  if (!data) throw new Error('No data returned from grant')
  return data
}

export async function revokeCapability(capabilityId: string): Promise<void> {
  const { error } = await invokeFunction('admin-users', {
    method: 'DELETE',
    path: `/capabilities/${capabilityId}`,
  })

  if (error) throw toApiRequestError(error, 'Failed to revoke capability')
}
